import { useState, useCallback } from 'react';
import { StackOperations } from '../services/stackOperations';
import { getChildrenItems } from '../services/getChildrenItems';

export function useStackLogic(debug = false, stackRootId = null) {
    const [selectedItems, setSelectedItems] = useState([]);
    const [lastSelectedItem, setLastSelectedItem] = useState(null);
    const [flattenedItems, setFlattenedItems] = useState([]);
    const [cardDataMap, setCardDataMap] = useState({});
    const [dragCompletedSignal, setDragCompletedSignal] = useState(0);
    const [selectedItemForDetail, setSelectedItemForDetail] = useState(null);

    const stackOps = new StackOperations(debug, stackRootId);

    const debugLog = useCallback((message, data) => {
        if (debug) {
            console.log(message, data);
            if (data) {
                alert(`DEBUG - ${message}:\n${typeof data === 'object' ?
                    Object.entries(data)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join('\n')
                    : data}`);
            } else {
                alert(`DEBUG - ${message}`);
            }
        }
    }, [debug]);

    // FIXED: Helper to refresh a single item's data from database
    const refreshItemData = useCallback(async (itemId) => {
        if (!itemId) return null;
        
        try {
            // Get fresh data from database by querying parent and finding the item
            // First, get the current item data to find its parent
            const currentData = cardDataMap[itemId];
            if (!currentData?.ed) {
                if (debug) console.log(`No parent (ed) found for item ${itemId}, skipping refresh`);
                return null;
            }
            
            // Fetch children of the parent to get fresh data
            const parentChildren = await getChildrenItems(currentData.ed);
            const freshItemData = parentChildren.find(child => child._id === itemId);
            
            if (freshItemData) {
                // Update cardDataMap with fresh data
                setCardDataMap(prev => ({
                    ...prev,
                    [itemId]: freshItemData
                }));
                
                if (debug) {
                    console.log(`Refreshed item ${itemId} data:`, freshItemData);
                }
                
                return freshItemData;
            }
            
            return null;
        } catch (error) {
            console.error(`Error refreshing item ${itemId}:`, error);
            return null;
        }
    }, [cardDataMap, debug]);

    const registerItem = useCallback((itemId, level, itemData) => {
        setFlattenedItems(prev => {
            const exists = prev.some(item => item.id === itemId);
            if (!exists) {
                return [...prev, { id: itemId, level }];
            }
            return prev;
        });

        if (itemId && itemData) {
            setCardDataMap(prev => {
                if (!prev[itemId] || JSON.stringify(prev[itemId]) !== JSON.stringify(itemData)) {
                    return {
                        ...prev,
                        [itemId]: itemData
                    };
                }
                return prev;
            });

            if (debug) {
                console.log(`Registered item ${itemId} with data:`, itemData);
            }
        }
    }, [debug]);

    const refreshAfterDrop = useCallback((draggedItemId, targetId, sourceParentId) => {
        if (debug) {
            console.log(`Refreshing tree after drop: ${draggedItemId} moved from ${sourceParentId} to ${targetId}`);
        }

        // FIXED: Clear flattened items to force re-registration
        setFlattenedItems([]);
        // FIXED: Update drag signal to trigger refresh in all StackItem components
        setDragCompletedSignal(Date.now());

        if (debug) {
            console.log("Folders to refresh:", [sourceParentId, targetId].filter(Boolean));
        }
        
        // FIXED: Additional refresh for root level changes
        if (targetId === stackRootId || sourceParentId === stackRootId) {
            if (debug) {
                console.log("Root level change detected, triggering additional refresh");
            }
            // Trigger another refresh after a brief delay to ensure root updates
            setTimeout(() => {
                setDragCompletedSignal(Date.now());
            }, 150);
        }
    }, [debug, stackRootId]);

    const handleDuplicateItem = useCallback(async () => {
        if (selectedItems.length === 0) {
            return;
        }

        const itemToDuplicate = selectedItems[0];

        try {
            const result = await stackOps.duplicateItem(itemToDuplicate, cardDataMap);
            
            refreshAfterDrop(result.newId, result.parentId, null);
            setSelectedItems([result.newId]);
        } catch (error) {
            console.error("Duplication failed:", error);
            alert(`Failed to duplicate item: ${error.message}`);
        }
    }, [selectedItems, cardDataMap, refreshAfterDrop, stackOps]);

    // FIXED: Modified handleNewItem to accept custom parentId parameter and trigger proper refresh
    const handleNewItem = useCallback((sourceId, title, customParentId = null) => () => {
        // Use custom parentId if provided, otherwise fall back to stackRootId
        const parentId = customParentId || stackRootId;
        
        if (!parentId) {
            console.error("No parent ID available to create new item");
            alert("No parent available for new item. Stack root ID is missing.");
            return;
        }

        if (debug) {
            console.log(`Creating new item with sourceId: ${sourceId}, title: ${title}, parentId: ${parentId}`);
        }

        stackOps.createNewItem(sourceId, title, parentId)
            .then(result => {
                if (debug) {
                    console.log(`New item created: ${result.newId} under parent: ${result.parentId}`);
                }
                
                // FIXED: Force refresh of the flattened items and trigger drag signal
                setFlattenedItems([]);
                setDragCompletedSignal(Date.now());
                
                // FIXED: If creating under stack root, we need to refresh the root level
                if (result.parentId === stackRootId) {
                    if (debug) {
                        console.log("Created item under stack root, triggering root refresh");
                    }
                    // Additional refresh signal specifically for root level
                    setTimeout(() => {
                        setDragCompletedSignal(Date.now());
                    }, 100);
                }
                
                // Select the new item
                setSelectedItems([result.newId]);
                setLastSelectedItem(result.newId);
            })
            .catch(error => {
                console.error("New item creation failed:", error);
                alert(`Failed to create new item: ${error.message}`);
            });
    }, [stackRootId, stackOps, debug, setFlattenedItems, setDragCompletedSignal, setSelectedItems, setLastSelectedItem]);

    // FIXED: Enhanced handleSelect that refreshes item data for detail view
    const handleSelect = useCallback(async (itemId, isShiftKey, isCtrlKey, showDetailView = false, onSelect = null) => {
        if (debug) {
            console.log("=== HANDLESELECT DEBUG ===");
            console.log("showDetailView:", showDetailView);
            console.log("itemId:", itemId);
            console.log("isShiftKey:", isShiftKey, "isCtrlKey:", isCtrlKey);
        }

        if (showDetailView && !isShiftKey && !isCtrlKey) {
            console.log("SHOULD SHOW DETAIL VIEW!");
            
            // FIXED: Get fresh item data from database before showing detail
            let itemData = cardDataMap[itemId];
            
            // If we don't have current data or we're showing detail view, refresh from DB
            if (!itemData || showDetailView) {
                const freshData = await refreshItemData(itemId);
                itemData = freshData || cardDataMap[itemId] || { _id: itemId };
                
                if (debug) {
                    console.log(`Fresh item data for detail view:`, itemData);
                }
            }
            
            setSelectedItemForDetail(itemData);
            setSelectedItems([itemId]);
            setLastSelectedItem(itemId);
            return;
        }

        if (isShiftKey && lastSelectedItem) {
            const startIdx = flattenedItems.findIndex(item => item.id === lastSelectedItem);
            const endIdx = flattenedItems.findIndex(item => item.id === itemId);

            if (startIdx >= 0 && endIdx >= 0) {
                const start = Math.min(startIdx, endIdx);
                const end = Math.max(startIdx, endIdx);

                const itemsInRange = flattenedItems.slice(start, end + 1).map(item => item.id);

                if (isCtrlKey) {
                    setSelectedItems(prev => {
                        const newSelection = [...prev];
                        itemsInRange.forEach(id => {
                            if (!newSelection.includes(id)) {
                                newSelection.push(id);
                            }
                        });
                        return newSelection;
                    });
                } else {
                    setSelectedItems(itemsInRange);
                }
            }
        } else if (isCtrlKey) {
            setSelectedItems(prev => {
                if (prev.includes(itemId)) {
                    return prev.filter(id => id !== itemId);
                } else {
                    return [...prev, itemId];
                }
            });
        } else {
            setSelectedItems([itemId]);
        }

        setLastSelectedItem(itemId);

        if (onSelect) {
            onSelect(itemId, isShiftKey, isCtrlKey);
        }
    }, [debug, cardDataMap, lastSelectedItem, flattenedItems, refreshItemData]);

    const handleBackToList = useCallback(() => {
        setSelectedItemForDetail(null);
    }, []);

    // FIXED: Enhanced handleDetailIdChange with better error handling and immediate feedback
    const handleDetailIdChange = useCallback(async (event) => {
        const newDetailId = event.target.value;

        if (!selectedItemForDetail?._id) {
            console.error("No item selected for detail view");
            return;
        }

        if (debug) {
            console.log(`Changing view preference for ${selectedItemForDetail._id} to ${newDetailId}`);
        }

        try {
            // Save to database first
            await stackOps.updateViewPreference(selectedItemForDetail._id, newDetailId);

            // Update local state immediately
            setCardDataMap(prev => ({
                ...prev,
                [selectedItemForDetail._id]: {
                    ...prev[selectedItemForDetail._id],
                    vi: newDetailId
                }
            }));

            setSelectedItemForDetail(prev => ({
                ...prev,
                vi: newDetailId
            }));

            if (debug) {
                console.log(`Successfully updated view preference to ${newDetailId}`);
            }

        } catch (error) {
            console.error("Failed to save view preference:", error);
            alert(`Failed to save view preference: ${error.message}`);
            
            // FIXED: Revert local state on failure
            const currentData = cardDataMap[selectedItemForDetail._id];
            if (currentData) {
                setSelectedItemForDetail(prev => ({
                    ...prev,
                    vi: currentData.vi
                }));
            }
        }
    }, [selectedItemForDetail, stackOps, cardDataMap, debug]);

    // FIXED: Enhanced refresh mechanism specifically for new item creation
    const refreshStackRoot = useCallback(() => {
        if (debug) {
            console.log("Refreshing stack root children");
        }
        setFlattenedItems([]);
        setDragCompletedSignal(Date.now());
    }, [debug]);

    // Programmatic methods for external control
    const selectItem = useCallback((itemId, showDetail = false) => {
        if (showDetail) {
            const itemData = cardDataMap[itemId] || { _id: itemId };
            setSelectedItemForDetail(itemData);
        }
        setSelectedItems([itemId]);
        setLastSelectedItem(itemId);
    }, [cardDataMap]);

    const selectDetailView = useCallback((itemId, viewId) => {
        const itemData = cardDataMap[itemId] || { _id: itemId };
        setSelectedItemForDetail(itemData);
        handleDetailIdChange({ target: { value: viewId } });
    }, [cardDataMap, handleDetailIdChange]);

    const clearSelection = useCallback(() => {
        setSelectedItems([]);
        setLastSelectedItem(null);
        setSelectedItemForDetail(null);
    }, []);

    return {
        // State
        selectedItems,
        lastSelectedItem,
        flattenedItems,
        cardDataMap,
        dragCompletedSignal,
        selectedItemForDetail,
        
        // Setters
        setSelectedItems,
        setLastSelectedItem,
        setFlattenedItems,
        setCardDataMap,
        setDragCompletedSignal,
        setSelectedItemForDetail,
        
        // Methods
        handleSelect,
        handleDuplicateItem,
        handleNewItem,
        refreshAfterDrop,
        registerItem,
        handleBackToList,
        handleDetailIdChange,
        debugLog,
        refreshStackRoot,
        
        // FIXED: Expose refreshItemData for external use
        refreshItemData,
        
        // Programmatic methods
        selectItem,
        selectDetailView,
        clearSelection,
        
        // Operations instance
        stackOps
    };
}